<template>
  <div
    class="flex items-center gap-3 bg-[var(--p-surface-100)] dark:bg-[var(--p-surface-800)] rounded-xl px-4 py-2 w-full mb-2"
  >
    <div class="flex items-center gap-3 flex-1 min-w-0">
      <div class="flex items-center gap-2">
        <div v-if="recorder.isRecording.value" class="flex items-center gap-2">
          <div class="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <Mic :size="16" class="text-red-500" />
        </div>
        <Mic v-else :size="16" class="text-[var(--p-surface-500)]" />
      </div>

      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium whitespace-nowrap">{{
            formatAudioDuration(
              playback.isPlaying.value ? playback.playbackTime.value : recorder.duration.value,
            )
          }}</span>
          <!-- Canvas para visualización de audio real -->
          <div class="flex-1 relative h-8">
            <canvas ref="canvasRef" class="absolute inset-0 w-full h-full rounded" />
          </div>
        </div>
      </div>
    </div>

    <div class="flex gap-2 items-center">
      <!-- Botón de reproducir (solo cuando hay audio grabado) -->
      <Button
        v-if="!recorder.isRecording.value && recorder.audioBlob.value"
        text
        rounded
        size="small"
        severity="primary"
        class="p-0.5!"
        @click="playback.toggle"
        v-tooltip.top="playback.isPlaying.value ? $t('whatsapp.actions.pause_audio') : $t('whatsapp.actions.play_audio')"
      >
        <template #icon>
          <component :is="playback.isPlaying.value ? Pause : Play" :size="20" />
        </template>
      </Button>

      <Button text rounded size="small" class="p-0.5!" severity="danger" @click="handleCancel" v-tooltip.top="$t('whatsapp.actions.delete_audio')">
        <template #icon>
          <Trash2 :size="20" />
        </template>
      </Button>

      <Button
        size="small"
        class="p-0.5!"
        v-if="!recorder.isRecording.value && recorder.audioBlob.value"
        rounded
        severity="primary"
        @click="handleSend"
        v-tooltip.top="$t('whatsapp.actions.send_audio')"
      >
        <template #icon>
          <SendHorizonal :size="20" />
        </template>
      </Button>

      <Button
        size="small"
        class="p-0.5!"
        v-if="recorder.isRecording.value"
        rounded
        severity="danger"
        @click="recorder.stopRecording"
        v-tooltip.top="$t('whatsapp.actions.stop_recording')"
      >
        <template #icon>
          <Square :size="16" />
        </template>
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from 'vue'
import Button from 'primevue/button'
import { Play, Pause, Trash2, Send, Square, Mic, SendHorizonal } from 'lucide-vue-next'
import { useAudioRecorder } from '../../composables/useAudioRecorder'
import { useAudioPlayback } from '../../composables/useAudioPlayback'
import { useWaveformVisualizer } from '../../composables/useWaveformVisualizer'
import { useTimeFormatter } from '../../composables/useTimeFormatter'
import { useI18n } from 'vue-i18n'
const emit = defineEmits<{
  'send-audio': [data: { blob: Blob; duration: number }]
  cancel: []
}>()

const { formatAudioDuration } = useTimeFormatter()
const { t } = useI18n()
const canvasRef = ref<HTMLCanvasElement | null>(null)

const recorder = useAudioRecorder({
  onStop: (blob) => {
    // Cargar audio para reproducción
    playback.load(blob)
    // Generar forma de onda estática
    nextTick(() => {
      visualizer.generateStaticWaveform()
    })
  },
  onError: () => {
    emit('cancel')
  },
})

const playback = useAudioPlayback()

const visualizer = useWaveformVisualizer(canvasRef, {
  barWidth: 3,
  gap: 2,
  sensitivity: 2.0,
  smoothingFactor: 0.25,
  minHeight: 6,
})

// Watchers para sincronizar visualización
watch(
  () => recorder.isRecording.value,
  (isRecording) => {
    if (isRecording && recorder.analyser.value && recorder.dataArray.value) {
      visualizer.startLiveAnimation(recorder.analyser.value, recorder.dataArray.value)
    } else {
      visualizer.stopAnimation()
    }
  },
)

watch(
  () => playback.isPlaying.value,
  (isPlaying) => {
    if (!isPlaying) {
      nextTick(() => {
        visualizer.drawStaticWaveform()
      })
    }
  },
)

watch(
  () => playback.playbackProgress.value,
  (progress) => {
    if (playback.isPlaying.value) {
      visualizer.drawPlaybackWaveform(progress)
    }
  },
)

const handleSend = () => {
  if (recorder.audioBlob.value) {
    emit('send-audio', {
      blob: recorder.audioBlob.value,
      duration: recorder.duration.value,
    })
  }
}

const handleCancel = () => {
  recorder.reset()
  playback.stop()
  emit('cancel')
}

onMounted(() => {
  recorder.startRecording()
})
</script>

<style scoped>
canvas {
  display: block;
}
</style>
